import { CountBadge } from '@vrplatform/components';
import { IconButton } from '@vrplatform/components-legacy';
import {
  useBilling,
  useEnabledFeatures,
  useRoutePermission,
} from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { ComponentType, ReactNode } from 'react';
import { lazy, Suspense, useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import { useTeamIssues } from '@shared/hooks';
import { useCanViewAuditHistory } from '../components/audit-history/_hooks/useCanViewAuditHistory';
import { useBankingAccess } from '../views/reconciliation/_hooks/useBankingAccess';

const ICON_SIZE = 18;

// The fee types and their gap counts load only with FEES_V2.
const FeesNavbarRoute = lazy(() =>
  import('./FeesNavbarRoute').then((module) => ({
    default: module.FeesNavbarRoute,
  }))
);

type NavbarRouteProps = {
  title: string;
  link: string;
  icon: ReactNode;
  active?: boolean;
  notification?: ReactNode;
  subRoutes?: {
    id: string;
    title: string;
    url: string;
    group: string;
    count?: number;
  }[];
  collapsible?: { storageKey: string };
};

export type PmNavigationProps = {
  NavbarRoute: ComponentType<NavbarRouteProps>;
  NavbarSyncsIconButton: ComponentType;
  NavigationSection: ComponentType<{
    children: ReactNode;
    title?: string;
  }>;
  QuickSearchRoute: ComponentType;
};

export const PmNavigation = ({
  NavbarRoute,
  NavbarSyncsIconButton,
  NavigationSection,
  QuickSearchRoute,
}: PmNavigationProps) => {
  const { isReadOnly } = useBilling();
  const { ACH, FEES_V2, SISS } = useEnabledFeatures();
  const bankingAccess = useBankingAccess();
  const canReadTransactions = useRoutePermission('transactions');
  const canReadReservations = useRoutePermission('reservations');
  const canReadListings = useRoutePermission('listings');
  const canReadOwners = useRoutePermission('owners');
  const canReadFeesCommissions = useRoutePermission('feesCommissions');
  const canReadReports = useRoutePermission('reports');
  const canReadStatements = useRoutePermission('statements');
  const canReadConnections = useRoutePermission('connections');
  const canReadFiles = useRoutePermission('files');
  const canViewActivity = useCanViewAuditHistory();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const type = usePageType();

  const getCurrentStatementsLink = () => {
    const base = '/statements/owner-statements';

    if (location.pathname.startsWith('/statement')) {
      const date = searchParams.get('date');

      return `${base}${date ? `?date=${date}` : ''}`;
    }

    return base;
  };

  return (
    <>
      <NavigationSection>
        <QuickSearchRoute />

        <NavbarRoute
          title="Dashboard"
          link="/dashboard/overview"
          icon={<Icon icon="DashboardIcon" size={ICON_SIZE} />}
          notification={isReadOnly ? null : <IssueCountBadge />}
        />
      </NavigationSection>

      <NavigationSection>
        {bankingAccess && (
          <NavbarRoute
            title={
              bankingAccess === 'paymentBatches'
                ? 'Payment batches'
                : ACH || SISS
                  ? 'Banking'
                  : 'Bank Reconciliation'
            }
            link="/reconciliation"
            icon={<Icon icon="FileInvoiceIcon" size={ICON_SIZE} />}
          />
        )}

        {canReadTransactions && (
          <NavbarRoute
            title="Deposits"
            link="/deposits"
            active={type === 'deposits'}
            icon={<Icon icon="PlusCircleIcon" size={ICON_SIZE} />}
          />
        )}
        {canReadTransactions && (
          <NavbarRoute
            title="Expenses"
            link="/expenses"
            active={type === 'expenses'}
            icon={<Icon icon="MinusCircleIcon" size={ICON_SIZE} />}
          />
        )}
        {canReadReservations && (
          <NavbarRoute
            title="Reservations"
            link="/reservations"
            icon={<Icon icon="CalendarEventIcon" size={ICON_SIZE} />}
          />
        )}
        {canReadListings && (
          <NavbarRoute
            title="Listings"
            link="/listings"
            active={type === 'listings'}
            icon={<Icon icon="HomeIcon" size={ICON_SIZE} />}
          />
        )}

        {canReadOwners && (
          <NavbarRoute
            title="Owners"
            link="/owners"
            active={type === 'owners'}
            icon={<Icon icon="UserIcon" size={ICON_SIZE} />}
          />
        )}

        {canReadFeesCommissions &&
          (FEES_V2 ? (
            <Suspense fallback={null}>
              <FeesNavbarRoute
                NavbarRoute={NavbarRoute}
                active={type === 'fees-commissions'}
              />
            </Suspense>
          ) : (
            <NavbarRoute
              title="Fees and Commissions"
              link="/fees-commissions"
              icon={<Icon icon="PercentageIcon" size={ICON_SIZE} />}
              active={type === 'fees-commissions'}
            />
          ))}

        {(canReadReports || canReadStatements) && (
          <NavbarRoute
            title="Reports"
            link="/reports"
            icon={<Icon icon="ListUnorderedIcon" size={ICON_SIZE} />}
          />
        )}
        {canReadStatements && (
          <NavbarRoute
            title="Statements"
            link={getCurrentStatementsLink()}
            icon={<Icon icon="ClipboardTextIcon" size={ICON_SIZE} />}
            active={type === 'statements'}
          />
        )}
      </NavigationSection>

      <NavigationSection>
        {canViewActivity && (
          <NavbarRoute
            title="Activity"
            link="/activity"
            icon={<Icon icon="HistoryIcon" size={ICON_SIZE} />}
          />
        )}
        {canReadConnections && (
          <NavbarRoute
            title="Connections"
            link="/connections"
            icon={<Icon icon="ShareIcon" size={ICON_SIZE} />}
            notification={<NavbarSyncsIconButton />}
            active={type === 'connections'}
          />
        )}

        {canReadFiles && (
          <NavbarRoute
            title="Files"
            link="/files"
            icon={<Icon icon="FolderOpenIcon" size={ICON_SIZE} />}
          />
        )}
        <NavbarRoute
          title="Settings"
          link="/settings"
          icon={<Icon icon="GearIcon" size={ICON_SIZE} />}
        />
      </NavigationSection>
    </>
  );
};

const usePageType = () => {
  const location = useLocation();

  return useMemo(() => {
    if (
      location.pathname.startsWith('/statements') ||
      location.pathname.startsWith('/statement/') ||
      location.pathname.startsWith('/pm-statement/')
    ) {
      return 'statements';
    }

    if (
      location.pathname.startsWith('/deposits') ||
      location.pathname.startsWith('/deposit/')
    ) {
      return 'deposits';
    }

    if (
      location.pathname.startsWith('/expenses') ||
      location.pathname.startsWith('/expense/') ||
      location.pathname.startsWith('/recurring/')
    ) {
      return 'expenses';
    }

    if (
      location.pathname.startsWith('/owners') ||
      location.pathname.startsWith('/owner/')
    ) {
      return 'owners';
    }

    if (
      location.pathname.startsWith('/listings') ||
      location.pathname.startsWith('/listing/')
    ) {
      return 'listings';
    }

    if (
      location.pathname.startsWith('/fees-commissions') ||
      location.pathname.startsWith('/fees-commission/')
    ) {
      return 'fees-commissions';
    }

    if (
      location.pathname.startsWith('/connections') ||
      location.pathname.startsWith('/connection/')
    ) {
      return 'connections';
    }
  }, [location.pathname]);
};

const IssueCountBadge = () => {
  const { aggregate } = useTeamIssues();

  if (!aggregate) return null;

  return (
    <IconButton sx={{ width: 'unset' }}>
      <CountBadge count={aggregate} loading={false} loadingColor="white" />
    </IconButton>
  );
};
